<script setup>
import { onMounted, ref, toRefs, onUnmounted, watch, nextTick } from "vue";

const stateHandler = state => ({
  loading() {
    state.value = "loading";
  },
  loaded() {
    state.value = "loaded";
  },
  complete() {
    state.value = "complete";
  },
  error() {
    state.value = "error";
  },
});

const initEmitter = (emit, stateHandler) => {
  return () => {
    stateHandler.loading();
    emit("infinite", stateHandler);
  };
};

const isVisible = (el, view) => {
  //まだDOMへの描画が完了していない場合はスキップする
  const elRect = el.getBoundingClientRect();
  if (elRect.height == 0) {
    return false;
  }
  if (!view) return elRect.top >= 0 && elRect.bottom <= window.innerHeight;
  const viewRect = view.getBoundingClientRect();
  return elRect.top >= viewRect.top && elRect.bottom <= viewRect.bottom;
};
const getParentElSearch = (elem) => {
  //直近のoverflow属性を持つ親を検索する
  let result = null;
  if (elem == null || elem.classList.contains('overflow-y-auto')) {
    result = elem;
  } else {
    result = getParentElSearch(elem.parentElement);
  }
  return result;
}
const getParentEl = () => {
  if (typeof infiniteLoading.value === 'undefined') {
    return null;
  }
  return getParentElSearch(infiniteLoading.value);
}
let observer;
const startObserver = params => {
  params.parentEl = getParentEl();
  let rootMargin = `0px 0px ${params.distance}px 0px`;
  if (params.top) rootMargin = `${params.distance}px 0px 0px 0px`;
  observer = new IntersectionObserver(
    entries => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        if (params.firstload) params.emit();
        params.firstload = true;
      }
    },
    { root: params.parentEl, rootMargin }
  );
  observer.observe(params.infiniteLoading.value);
};
const stopObserver = () => {
  observer.disconnect();
};

const emit = defineEmits(["infinite"]);
const props = defineProps({
  top: { type: Boolean, required: false },
  distance: { type: Number, required: false, default: 0 },
  identifier: { required: false },
  execution: { required: false },
  firstload: { type: Boolean, required: false, default: true },
  slots: { type: Object, required: false },
  listType: { type:String,default:()=> { return 'list'}}
});
const prevHeight = 0;
const infiniteLoading = ref(null);
const state = ref("ready");
const { top, firstload, distance } = props;
const { identifier,execution } = toRefs(props);
const params = {
  infiniteLoading,
  top,
  firstload,
  distance,
  emit: initEmitter(emit, stateHandler(state)),
  parentEl: null,
  prevHeight:0,
};
const stateWatcher = () =>
  watch(state, async newVal => {
    const parentEl = params.parentEl || document.documentElement;
    await nextTick();
    if (newVal == "loaded" && top)
      parentEl.scrollTop = parentEl.scrollHeight - params.prevHeight;
      params.prevHeight = parentEl.scrollHeight;
    if (newVal == "loaded" && isVisible(infiniteLoading.value, params.parentEl))
      params.emit();
    if (newVal == "complete") stopObserver();
  });
const identifierWatcher = () =>
  watch(identifier, () => {
    state.value = "ready";
    stopObserver();
    startObserver(params);
  });
const executionWatcher = () =>
  watch(execution, () => {
    state.value = "ready";
    params.emit();
  });
onMounted(() => {
  startObserver(params);
  stateWatcher();
  if (identifier) identifierWatcher();
  if (execution) executionWatcher();
});
onUnmounted(() => {
  stopObserver();
});
</script>

<template>
  <div v-if="listType == 'list'" ref="infiniteLoading">
    <slot v-if="state == 'loading'" name="spinner">
    </slot>
    <slot v-if="state == 'complete'" name="complete">
      <span> {{ slots?.complete || "No more results!" }} </span>
    </slot>
    <slot v-if="state == 'error'" name="error" :retry="params.emit">
      <span class="state-error">
        <span>{{ slots?.error || "Oops something went wrong!" }}</span>
        <button class="retry" @click="params.emit">retry</button>
      </span>
    </slot>
  </div>
  <tr v-else-if="listType == 'table'" ref="infiniteLoading">
    <slot v-if="state == 'loading'" name="spinner">
    </slot>
    <slot v-if="state == 'complete'" name="complete">
      <span> {{ slots?.complete || "No more results!" }} </span>
    </slot>
    <slot v-if="state == 'error'" name="error" :retry="params.emit">
      <span class="state-error">
        <span>{{ slots?.error || "Oops something went wrong!" }}</span>
        <button class="retry" @click="params.emit">retry</button>
      </span>
    </slot>
  </tr>
</template>

<style scoped>
.state-error {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.retry {
  margin-top: 8px;
  padding: 2px 6px 4px 6px;
  width: 60px;
  color: inherit;
  font-size: 14px;
  font-family: inherit;
  background: transparent;
  border: 2px solid currentColor;
  border-radius: 5px;
  outline: none;
  cursor: pointer;
}
.retry:hover {
  opacity: 0.8;
}
</style>
